import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { MediaState, MediaUploadResponse } from '../../types';
import { mediaService } from '../../services/mediaService';

const initialState: MediaState = {
  uploadedFiles: [],
  uploadProgress: 0,
  isLoading: false,
  error: null,
};

// Async Thunks
export const uploadMedia = createAsyncThunk(
  'media/upload',
  async ({ files, containerName }: { files: File[]; containerName: string }, { rejectWithValue }) => {
    try {
      const response = await mediaService.uploadMedia(files, containerName);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Upload failed');
    }
  }
);

export const uploadSingleMedia = createAsyncThunk(
  'media/uploadSingle',
  async ({ file, containerName }: { file: File; containerName: string }, { rejectWithValue }) => {
    try {
      const response = await mediaService.uploadSingleMedia(file, containerName);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Upload failed');
    }
  }
);

export const deleteMedia = createAsyncThunk(
  'media/delete',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      await mediaService.deleteMedia(mediaId);
      return mediaId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Delete failed');
    }
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUploadedFiles: (state) => {
      state.uploadedFiles = [];
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
    resetUploadProgress: (state) => {
      state.uploadProgress = 0;
    },
  },
  extraReducers: (builder) => {
    // Upload Media
    builder
      .addCase(uploadMedia.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 100;
        // The response contains array of media IDs
        // We'll store them for now, you might want to fetch full media details later
      })
      .addCase(uploadMedia.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.uploadProgress = 0;
      });

    // Upload Single Media
    builder
      .addCase(uploadSingleMedia.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadSingleMedia.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 100;
      })
      .addCase(uploadSingleMedia.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.uploadProgress = 0;
      });

    // Delete Media
    builder
      .addCase(deleteMedia.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadedFiles = state.uploadedFiles.filter(file => file.id !== action.payload);
      })
      .addCase(deleteMedia.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUploadedFiles, setUploadProgress, resetUploadProgress } = mediaSlice.actions;
export default mediaSlice.reducer;
